import React, { useEffect, useRef, memo } from "react";
import ApexCharts from "apexcharts";
import { THEME_COLORS } from "../../../../../color";
import { useSelector } from "react-redux";
const VerticalBarChart = ({
  icon,
  title,
  data,
  legend,
  categories,
  classTitle = "",
  heading,
  xheading,
  yheading,
  min,
  max,
  tickAmount,
  xAxisTitle,
  yAxisTitle,
  series,
  showPercentage = true,
  roundDataLabels = false,
  showLegend = true,
  colors,
  height = 350,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const { user } = useSelector((state) => state.auth);
  const isPermissionCompleted = user?.isCompleted;
  //console.log(isPermissionCompleted, "isPermissionCompleted");

  const colorSequence = colors || [
    THEME_COLORS.green,
    THEME_COLORS.yellow,
    THEME_COLORS.darkblue,
    THEME_COLORS.pink,
  ];

  const barColors = categories.map(
    (_, index) => colorSequence[index % colorSequence.length],
  );

  useEffect(() => {
    if (!chartRef.current) return;

    const allValues = series
      ? series.flatMap((s) => s.data || [])
      : data
        ? data
        : [];
    const realMaxVal = Math.max(
      ...allValues.map((v) =>
        typeof v === "object" ? (v?.y ?? v?.value ?? 0) : v || 0,
      ),
      0,
    );

    let calculatedMax = max;
    if (max === undefined && realMaxVal > 0) {
      const valWithHeadroom = realMaxVal * 1.08;
      if (realMaxVal >= 100) {
        calculatedMax = Math.ceil(valWithHeadroom / 100) * 100;
      } else if (realMaxVal >= 10) {
        calculatedMax = Math.ceil(valWithHeadroom / 10) * 10;
      } else {
        calculatedMax = Math.ceil(valWithHeadroom);
      }
    }

    const options = {
      chart: {
        type: "bar",
        toolbar: { show: false },
        height: height,
        zoom: { enabled: false },
        offsetY: 0,
        parentHeightOffset: 0,
      },
      series: series || [{ name: legend, data }],
      xaxis: {
        categories,
        title: {
          text: xheading || xAxisTitle || "",
          style: {
            fontSize: "12px",
            color: "#000",
            fontWeight: "normal",
            marginTop: "-100px",
          },
        },
        labels: {
          style: { fontSize: "11px", color: "#000", fontWeight: "normal" },
        },
        axisBorder: { show: true, color: "#000" },
      },
      yaxis: {
        min: min !== undefined ? min : 0,
        max: calculatedMax !== undefined ? calculatedMax : undefined,
        tickAmount: tickAmount !== undefined ? tickAmount : undefined,
        title: {
          text: yheading || yAxisTitle || "",
          style: { fontSize: "12px", color: "#000", fontWeight: "normal" },
        },
        labels: {
          style: { fontSize: "11px", color: "#000", fontWeight: "normal" },
          formatter: (val) => {
            if (showPercentage) return `${val}%`;
            if (val === 0) return "0";
            if (val % 1 !== 0) {
              return parseFloat(val.toFixed(4));
            }
            return val;
          },
        },
        axisBorder: { show: true, color: "#000" },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val, opts) => {
            // Always show actual/raw value in tooltip
            const seriesObj = opts?.w?.config?.series?.[opts.seriesIndex];
            if (seriesObj && seriesObj.originalValues) {
              const originalVal = seriesObj.originalValues[opts.dataPointIndex];
              return showPercentage ? `${originalVal}%` : originalVal;
            }
            const dataPoint =
              opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[
                opts.dataPointIndex
              ];
            if (
              dataPoint &&
              typeof dataPoint === "object" &&
              dataPoint.originalValue !== undefined
            ) {
              return showPercentage
                ? `${dataPoint.originalValue}%`
                : dataPoint.originalValue;
            }
            return showPercentage ? `${val}%` : val;
          },
          title: () => "",
        },
      },
      colors: barColors,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "45%",
          endingShape: "rounded",
          distributed: (series?.length || 1) <= 1,
          dataLabels: { position: "top" },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -14,
        style: {
          fontSize: "10px",
          colors: ["#06513D"],
          fontWeight: "normal",
        },
        formatter: function (val, opts) {
          // Show values rounded to 2 decimal places over bars when roundDataLabels is true
          const roundVal = (v) =>
            roundDataLabels ? parseFloat(Number(v).toFixed(2)) : v;
          const seriesObj = opts?.w?.config?.series?.[opts.seriesIndex];
          if (seriesObj && seriesObj.originalValues) {
            const originalVal = seriesObj.originalValues[opts.dataPointIndex];
            const displayVal = roundVal(originalVal);
            return showPercentage ? `${displayVal}%` : displayVal;
          }
          const dataPoint =
            opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[
              opts.dataPointIndex
            ];
          if (
            dataPoint &&
            typeof dataPoint === "object" &&
            dataPoint.originalValue !== undefined
          ) {
            const displayVal = roundVal(dataPoint.originalValue);
            return showPercentage ? `${displayVal}%` : displayVal;
          }
          const displayVal = roundVal(val);
          return showPercentage ? `${displayVal}%` : displayVal;
        },
      },
      grid: {
        yaxis: { lines: { show: false } },
        xaxis: { lines: { show: false } },
        borderColor: "#E5E5E5",
      },
      legend: {
        show: showLegend,
        position: "bottom",
        horizontalAlign: "center",
        fontSize: "11px",
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
      responsive: [
        {
          breakpoint: 1280,
          options: {
            chart: { height: 330 },
            xaxis: { labels: { style: { fontSize: "10px" }, rotate: -25 } },
            yaxis: { labels: { style: { fontSize: "10px" } } },
          },
        },
        {
          breakpoint: 1024,
          options: {
            chart: { height: 300 },
            plotOptions: { bar: { columnWidth: "55%" } },
            xaxis: { labels: { style: { fontSize: "9.5px" }, rotate: -30 } },
            yaxis: { labels: { style: { fontSize: "9.5px" } } },
          },
        },
        {
          breakpoint: 768,
          options: {
            chart: { height: 260 },
            xaxis: { labels: { style: { fontSize: "9px" }, rotate: -40 } },
            yaxis: { labels: { style: { fontSize: "9px" } } },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: { height: 220 },
            plotOptions: { bar: { columnWidth: "65%" } },
            xaxis: { labels: { style: { fontSize: "8px" }, rotate: -45 } },
            yaxis: { labels: { style: { fontSize: "8px" } } },
          },
        },
      ],
    };

    if (!chartInstance.current) {
      chartInstance.current = new ApexCharts(chartRef.current, options);
      chartInstance.current.render();
    } else {
      chartInstance.current.updateOptions({
        series: series || [{ name: legend, data }],
        xaxis: { categories },
        colors: barColors,
        plotOptions: {
          bar: {
            distributed: (series?.length || 1) <= 1,
          },
        },
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [data, legend, categories, min, max, tickAmount, series]);

  return (
    <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 ">
      {/* Optional Heading */}
      {heading && (
        <h2 className="text-left text-[#06513D] font-bold text-sm md:text-base mb-2">
          {heading}
        </h2>
      )}

      {(icon || title) && (
        <div className="flex items-center gap-2 justify-start mb-2 min-h-[48px]">
          <span className="text-[#06513D] text-xl sm:text-2xl md:text-3xl">
            {icon}
          </span>
          <h2
            className={`font-bold capitalize text-base sm:text-lg text-[#06513D]  ${classTitle}`}
          >
            {title}
          </h2>
        </div>
      )}

      {/* Chart */}
      <div
        ref={chartRef}
        className={`${
          !isPermissionCompleted ? "blur-sm opacity-60 pointer-events-none" : ""
        }`}
      ></div>
    </div>
  );
};

export default memo(VerticalBarChart);
